/**
 * FinanceAuditModal — отчёт «Проверить и исправить ошибки» в финансах.
 *
 * Показывает список найденных проблем (несовпадение amo_lead_id, авто-имена
 * production-карточек, дубликаты), даёт чекбоксы для выбора фиксов и применяет
 * через POST /api/finance/audit/apply.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  runFinanceAudit, applyFinanceAudit,
  type AuditIssue, type AuditReport,
} from "@/api/finance";
import { Icon } from "@/components/prototype/Icon";


interface Props {
  open: boolean;
  onClose: () => void;
}

const TYPE_LABELS: Record<string, string> = {
  wrong_amo_lead: "Неверная связь с AmoCRM",
  auto_name:      "Авто-имя в карточке",
  duplicate_pp:   "Дубликаты карточек",
};
const TYPE_COLORS: Record<string, string> = {
  wrong_amo_lead: "var(--brick)",
  auto_name:      "#c97a1f",
  duplicate_pp:   "#5d4ec1",
};


export function FinanceAuditModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const [report, setReport] = useState<AuditReport | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const auditMut = useMutation({
    mutationFn: () => runFinanceAudit(),
    onSuccess: (r) => {
      setReport(r);
      // По умолчанию все auto-фиксы выбраны
      setSelected(new Set(r.issues.map((_, i) => i).filter(i => r.issues[i].severity === "auto")));
      setError(null);
    },
    onError: (e: Error) => setError(e.message),
  });

  const applyMut = useMutation({
    mutationFn: (fixes: AuditIssue[]) => applyFinanceAudit(fixes),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["finance-objects"] });
      void qc.invalidateQueries({ queryKey: ["production"] });
      alert(`Применено: ${r.applied}\nОшибок: ${r.failed}`);
      // Перезапускаем аудит для актуальной картины
      auditMut.mutate();
    },
    onError: (e: Error) => setError(e.message),
  });

  // Запускаем аудит при открытии
  useEffect(() => {
    if (open && !report) auditMut.mutate();
    if (!open) { setReport(null); setSelected(new Set()); setError(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const grouped = useMemo(() => {
    const m = new Map<string, { issue: AuditIssue; idx: number }[]>();
    report?.issues.forEach((issue, idx) => {
      const list = m.get(issue.type) || [];
      list.push({ issue, idx });
      m.set(issue.type, list);
    });
    return m;
  }, [report]);

  function toggle(i: number) {
    setSelected(s => {
      const n = new Set(s);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });
  }

  function toggleGroup(type: string, on: boolean) {
    setSelected(s => {
      const n = new Set(s);
      report?.issues.forEach((issue, i) => {
        if (issue.type === type) on ? n.add(i) : n.delete(i);
      });
      return n;
    });
  }

  function applySelected() {
    if (!report || selected.size === 0) return;
    const fixes = Array.from(selected).map(i => report.issues[i]);
    applyMut.mutate(fixes);
  }

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-window" onClick={(e) => e.stopPropagation()}
           style={{ maxWidth: 760, width: "92vw", maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Аудит финансов</h2>
            {report && (
              <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 4 }}>
                Найдено проблем: <strong style={{ color: "var(--ink-1)" }}>{report.summary.total}</strong>
                {report.summary.total > 0 && <> · авто-исправляемых: <strong style={{ color: "var(--green)" }}>{report.summary.auto_fixable}</strong></>}
              </div>
            )}
          </div>
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Закрыть</button>
        </div>

        {/* Body */}
        <div style={{ padding: "12px 20px", overflowY: "auto", flex: 1 }}>
          {auditMut.isPending && <div className="muted" style={{ padding: 24, textAlign: "center" }}>Анализирую…</div>}
          {error && <div className="alert alert-red"><div className="alert-body">{error}</div></div>}
          {report && report.issues.length === 0 && (
            <div style={{ textAlign: "center", padding: "32px 16px", color: "var(--green)" }}>
              <div style={{ marginBottom: 8 }}><Icon name="check_circle" size={32}/></div>
              <div style={{ fontSize: 14 }}>Несоответствий не найдено. Данные в порядке.</div>
            </div>
          )}
          {report && report.issues.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {Array.from(grouped.entries()).map(([type, items]) => {
                const allSelected = items.every(it => selected.has(it.idx));
                const someSelected = items.some(it => selected.has(it.idx));
                return (
                  <div key={type} style={{ border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden" }}>
                    <div style={{
                      padding: "10px 14px", background: "var(--paper-2)",
                      borderLeft: `4px solid ${TYPE_COLORS[type] || "var(--mute)"}`,
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}>
                      <div>
                        <strong style={{ fontSize: 13 }}>{TYPE_LABELS[type] || type}</strong>
                        <span style={{ marginLeft: 8, fontSize: 11, color: "var(--mute)" }}>· {items.length} шт.</span>
                      </div>
                      <label style={{ fontSize: 11, color: "var(--mute)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }}
                          onChange={(e) => toggleGroup(type, e.target.checked)}
                        />
                        выбрать все
                      </label>
                    </div>
                    {items.map(({ issue, idx }) => (
                      <label key={idx} style={{
                        display: "flex", gap: 10, padding: "10px 14px",
                        borderTop: "1px solid var(--line)", cursor: "pointer",
                        background: selected.has(idx) ? "rgba(178,52,40,0.04)" : "transparent",
                      }}>
                        <input
                          type="checkbox"
                          checked={selected.has(idx)}
                          onChange={() => toggle(idx)}
                          style={{ marginTop: 3, flexShrink: 0 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 3 }}>{issue.title}</div>
                          <div style={{ fontSize: 12, color: "var(--mute)", lineHeight: 1.4 }}>{issue.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <button className="btn btn-sm" onClick={() => auditMut.mutate()} disabled={auditMut.isPending}>
            {auditMut.isPending ? "…" : "↻ Перезапустить"}
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-sm btn-ghost" onClick={onClose}>Отмена</button>
            <button
              className="btn btn-sm btn-primary"
              onClick={applySelected}
              disabled={selected.size === 0 || applyMut.isPending}
            >
              {applyMut.isPending ? "Применяю…" : `Применить (${selected.size})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
